import { Signal2, Listener2 } from "./event/Signal2";
import { Disposable } from "./Disposable";
//
// Flambe - Rapid game development
// https://github.com/aduros/flambe/blob/master/LICENSE.txt

/**
 * Wraps a single value, notifying listeners when the value changes.
 */
export class Value<A> {
  constructor(value: A, listener?: Listener2<A, A>) {
    this._value = value;
    this._changed = listener != null ? new Signal2(listener) : null;
  }

  /**
   * Immediately calls a listener with the current value, and again whenever the value changes.
   * @returns A handle that can be disposed to stop watching for changes.
   */
  public watch(listener: Listener2<A, A>): Disposable {
    listener(this._value, this._value);
    return this.changed.connect(listener);
  }

  /**
   * The wrapped value, setting this to a different value will fire the `changed` signal.
   */
  public get _(): A {
    return this._value;
  }

  /**
   * The wrapped value, setting this to a different value will fire the `changed` signal.
   */
  public set _(newValue: A) {
    var oldValue = this._value;
    if (newValue != oldValue) {
      this._value = newValue;
      if (this._changed != null) {
        this._changed.emit(newValue, oldValue);
      }
    }
  }

  /**
   * Emitted when the value has changed. The first listener parameter is the new current value,
   * the second parameter is the old previous value.
   */
  get changed() {
    if (this._changed == null) {
      this._changed = new Signal2();
    }
    return this._changed;
  }

  private _value: A;
  private _changed: Signal2<A, A> | null;
}
